import { EmployeeAvatar } from "@/components/employee/EmployeeAvatar.tsx";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import EmptyStateNothingFound from "@/components/empty-state/EmptyStateNothingFound.tsx";
import { ReminderSendModal } from "@/components/reminder/ReminderSendModal.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { ReminderEntityType } from "@/types/reminder.types.ts";
import { ScoreTag } from "@/pages/talent/performance-reviews/view/components/ScoreTag.tsx";
import { PerformanceReviewEditReviewersModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewEditReviewersModal.tsx";
import { PerformanceReviewParticipantShareSettingModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewParticipantShareSettingModal.tsx";
import { PerformanceReviewParticipantSummaryModal } from "@/pages/talent/performance-reviews/view/widgets/PerformanceReviewParticipantSummaryModal.tsx";
import { usePerformanceReviewParticipantsSummarySearch } from "@/queries/performance-reviews/use-performance-reviews.query.ts";
import { ApiSorting } from "@/types/api.types.ts";
import { EmployeeColumnId } from "@/types/column.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  ApiPerformanceReview,
  ApiPerformanceReviewParticipantShareSetting,
  ApiPerformanceReviewParticipantsSummaryFilter,
  ApiPerformanceReviewParticipantSummarySearchItem,
  ApiPerformanceReviewReviewerTypes,
  ApiPerformanceReviewReviewerTypeSummarySearchItem,
  ApiPerformanceReviewStatus,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { ApiEmployee } from "@/types/employees/employee.types.ts";
import {
  Box,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  PaginationBlock,
  Stack,
  Table,
  TableCell,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, Row } from "@tanstack/react-table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";

export const PerformanceReviewParticipantsSummary = ({
  review,
  filter,
  filterCount,
  sorting,
  onApplySorting,
}: {
  review: ApiPerformanceReview;
  filter: ApiPerformanceReviewParticipantsSummaryFilter;
  filterCount: number;
  sorting?: ApiSorting;
  onApplySorting: (sorting: ApiSorting) => void;
}) => {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const { renderEmployeeName, renderTimeAgo } = useFormatter();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") ?? "");
  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (value) => {
      setSearchParams((params) => {
        if (value) params.set("search", value);
        else params.delete("search");
        params.set("page", "1");
        return params;
      });
    },
  });

  const { data: participantData, isLoading } = usePerformanceReviewParticipantsSummarySearch(
    review.id,
    filter
  );

  const handleOpenParticipantSummary = useCallback(
    (employee_id: number) => {
      openModal(PerformanceReviewParticipantSummaryModal, {
        performance_review_id: review.id,
        employee_id: employee_id,
      });
    },
    [review]
  );

  const handleOpenEditReviewersModal = useCallback(
    (employeeId: number, reviewerTypeId: number) => {
      openModal(PerformanceReviewEditReviewersModal, {
        performanceReviewId: review.id,
        employeeId: employeeId,
        reviewerTypeId: reviewerTypeId,
      });
    },
    [review]
  );

  const handleOpenParticipantShareSetting = useCallback(
    (employee: ApiEmployee, shareSetting: ApiPerformanceReviewParticipantShareSetting) => {
      openModal(PerformanceReviewParticipantShareSettingModal, {
        performanceReviewId: review.id,
        employee: employee,
        shareSetting: shareSetting,
      });
    },
    [review]
  );

  const columnHelper = createColumnHelper<ApiPerformanceReviewParticipantSummarySearchItem>();
  const columns = useMemo<ColumnDef<ApiPerformanceReviewParticipantSummarySearchItem>[]>(() => {
    const inner: ColumnDef<ApiPerformanceReviewParticipantSummarySearchItem>[] = [];
    if (!participantData) return inner;

    inner.push(
      {
        id: EmployeeColumnId.employee_name,
        accessorKey: "employee",
        enableSorting: true,
        sortDescFirst: false,
        header: t("core:full_name"),
        cell: (cell) => <EmployeeItem employee={cell.row.original.employee} enablePreview />,
        ...ColumnSizes.employee,
      },
      columnHelper.display({
        id: "department",
        header: t("core:department"),
        cell: (cell) => (
          <TableCell variant={"secondary"}>
            {cell.row.original.employee.employee_position?.department?.title || "-"}
          </TableCell>
        ),
        ...ColumnSizes.standard,
      }),

      columnHelper.display({
        id: "responses",
        header: t("performance_review:performance_reviews"),
        cell: (cell) => {
          const totalCount = cell.row.original.overall_summary.total_count;
          const completedCount = cell.row.original.overall_summary.completed_count;

          return (
            <span className={" text-secondary whitespace-nowrap"}>
              {t("common:of", { current: completedCount, total: totalCount })}
            </span>
          );
        },
        ...ColumnSizes.status,
      }),

      columnHelper.display({
        id: "progress",
        header: t("common:progress"),
        cell: (cell) => {
          const totalCount = cell.row.original.overall_summary.total_count;
          const completedCount = cell.row.original.overall_summary.completed_count;
          return (
            <Box
              className={twMerge(
                "h-1 rounded-xs bg-dark/10 overflow-hidden relative max-w-32 mr-10"
              )}
            >
              <Box
                style={{
                  width: totalCount == 0 ? 0 : (completedCount / totalCount) * 100 + "%",
                }}
                className={twMerge("absolute left-0 h-full rounded-xs bg-brand-dark")}
              ></Box>
            </Box>
          );
        },
        ...ColumnSizes.standard,
      })
    );

    for (const reviewer_type of participantData.config.reviewer_types) {
      inner.push(
        columnHelper.display({
          id: `reviewer-type-${reviewer_type.id}`,
          header:
            PerformanceReviewReviewerTypeItems().find((item) => item.id == reviewer_type.type)
              ?.shortTitle ??
            reviewer_type.title ??
            t("common:no_title"),
          cell: (cell) => {
            const reviewerTypeSummary = cell.row.original.reviewer_type_summaries.find(
              (item) => item.reviewer_type.id === reviewer_type.id
            );
            const currentSummary = reviewerTypeSummary?.summary;
            return (
              <Stack
                items={"start"}
                className={twMerge("cursor-pointer")}
                onClick={() => handleOpenParticipantSummary(cell.row.original.employee.id)}
              >
                <Tooltip>
                  <TooltipTrigger>
                    <ScoreTag
                      score={currentSummary?.rating.avg_scale_score}
                      emptyText={currentSummary?.total_count === 0 ? t("common:n_a") : undefined}
                      isPending={
                        currentSummary &&
                        currentSummary.total_count > 0 &&
                        currentSummary.completed_count < currentSummary.total_count
                      }
                      onClick={() => {}}
                    />
                  </TooltipTrigger>
                  {currentSummary && currentSummary.total_count > 0 && (
                    <TooltipContent>
                      <span>
                        {t("common:of", {
                          current: currentSummary.completed_count,
                          total: t("plurals:responses_count", {
                            count: currentSummary.total_count,
                          }),
                        })}
                      </span>
                    </TooltipContent>
                  )}
                </Tooltip>
              </Stack>
            );
          },
          ...ColumnSizes.narrow,
        })
      );

      if (reviewer_type.type === ApiPerformanceReviewReviewerTypes.manager) {
        inner.push(
          columnHelper.display({
            id: "manager_setup",
            header: t("performance_review:set_manager"),
            cell: (cell) => {
              const manager =
                cell.row.original.assigned_manager ||
                cell.row.original.employee?.employee_position?.manager;
              return (
                <Stack direction={"horizontal"} items={"center"} gap={"sm"}>
                  {manager && (
                    <Stack
                      gap={"xs"}
                      direction={"horizontal"}
                      items={"center"}
                      className={"text-ellipsis overflow-hidden"}
                    >
                      <EmployeeAvatar employee={manager} size={"sm"} />
                      <Text className={"whitespace-nowrap"}>{renderEmployeeName(manager)}</Text>
                      {cell.row.original.assigned_manager && (
                        <Tooltip>
                          <TooltipTrigger>
                            <Text className={"text-warning"}>*</Text>
                          </TooltipTrigger>
                          <TooltipContent>{t("common:edited")}</TooltipContent>
                        </Tooltip>
                      )}
                    </Stack>
                  )}
                </Stack>
              );
            },
            ...ColumnSizes.wide,
          })
        );
      }

      if (
        [ApiPerformanceReviewReviewerTypes.peer, ApiPerformanceReviewReviewerTypes.custom].includes(
          reviewer_type.type
        )
      ) {
        const key =
          ApiPerformanceReviewReviewerTypes.peer === reviewer_type.type
            ? "peers_setup"
            : `custom_${reviewer_type.id}_setup`;
        const title =
          ApiPerformanceReviewReviewerTypes.peer === reviewer_type.type
            ? t("performance_review:setup_peers")
            : t("performance_review:setup_peers_in_custom", { name: reviewer_type.title });

        inner.push(
          columnHelper.display({
            id: key,
            header: title,
            cell: (cell) => {
              const currentSummary = cell.row.original.reviewer_type_summaries.find(
                (item) => item.reviewer_type.type === reviewer_type.type
              );
              const reviewerType = currentSummary?.reviewer_type;

              if (!currentSummary || !reviewerType) {
                return null;
              }

              return (
                <Button
                  size={"sm"}
                  variant={"secondary"}
                  onClick={() =>
                    handleOpenEditReviewersModal(cell.row.original.employee.id, reviewerType.id)
                  }
                >
                  {t("plurals:peers", { count: currentSummary?.summary.total_count ?? 0 })}
                </Button>
              );
            },
            ...ColumnSizes.narrow,
          })
        );
      }
    }

    inner.push(
      columnHelper.display({
        id: "overall",
        header: t("performance_review:overall"),
        cell: (cell) => {
          const currentSummary = cell.row.original.overall_summary;
          return (
            <Stack
              items={"start"}
              className={twMerge("cursor-pointer")}
              onClick={() => handleOpenParticipantSummary(cell.row.original.employee.id)}
            >
              <Tooltip>
                <TooltipTrigger>
                  <ScoreTag
                    score={currentSummary.rating.avg_scale_score}
                    onClick={() => {}}
                    emptyText={currentSummary.total_count === 0 ? t("common:n_a") : undefined}
                    isPending={
                      currentSummary.total_count > 0 &&
                      currentSummary.completed_count < currentSummary.total_count
                    }
                  />
                </TooltipTrigger>
                {currentSummary.total_count > 0 && (
                  <TooltipContent>
                    <span>
                      {t("common:of", {
                        current: currentSummary.completed_count,
                        total: t("plurals:responses_count", { count: currentSummary.total_count }),
                      })}
                    </span>
                  </TooltipContent>
                )}
              </Tooltip>
            </Stack>
          );
        },
        ...ColumnSizes.narrow,
      })
    );

    return inner;
  }, [participantData]);

  const renderRowAction = useCallback(
    (row: Row<ApiPerformanceReviewParticipantSummarySearchItem>) => {
      const item = row.original;

      const isSetupEnable = (
        reviewerTypeSummary: ApiPerformanceReviewReviewerTypeSummarySearchItem
      ) => {
        const isEditableType = [
          ApiPerformanceReviewReviewerTypes.peer,
          ApiPerformanceReviewReviewerTypes.custom,
        ].includes(reviewerTypeSummary.reviewer_type.type);

        const isEditableStatus = [
          ApiPerformanceReviewStatus.draft,
          ApiPerformanceReviewStatus.pending_launch,
          ApiPerformanceReviewStatus.running,
          ApiPerformanceReviewStatus.paused,
        ].includes(review.status);

        return isEditableType && isEditableStatus;
      };

      const managerType = item.reviewer_type_summaries.find(
        (s) => s.reviewer_type.type === ApiPerformanceReviewReviewerTypes.manager
      );

      return (
        <DropdownMenu>
          <DropdownMenuItem
            to={`/performance-reviews/${review.id}/view/participants/${item.employee.id}`}
            target={"_blank"}
            title={t("common:view")}
          />
          {item.share_setting && (
            <DropdownMenuItem
              onClick={() => handleOpenParticipantShareSetting(item.employee, item.share_setting!)}
              title={t("performance_review:share")}
            />
          )}
          {managerType && (
            <DropdownMenuItem
              onClick={() =>
                handleOpenEditReviewersModal(item.employee.id, managerType.reviewer_type.id)
              }
              title={t("performance_review:change_manager")}
            />
          )}
          {item.reviewer_type_summaries
            .filter((summary) => isSetupEnable(summary))
            .map((summary) => {
              const title =
                PerformanceReviewReviewerTypeItems().find((i) => i.id == summary.reviewer_type.type)
                  ?.shortTitle ??
                summary.reviewer_type.title ??
                t("common:no_title");

              return (
                <DropdownMenuItem
                  key={`button-edit-type-${summary.reviewer_type.id}`}
                  onClick={() =>
                    handleOpenEditReviewersModal(item.employee.id, summary.reviewer_type.id)
                  }
                  title={`${t("common:setup")} ${title}`}
                />
              );
            })}
          {review.status !== ApiPerformanceReviewStatus.completed && (
            <DropdownMenuItem
              onClick={() =>
                openModal(ReminderSendModal, {
                  recipients: [item.employee],
                  entityType: ReminderEntityType.performance_review,
                  entityIds: [review.id],
                })
              }
              title={t("core:remind")}
              description={
                item.datetime_last_reminder
                  ? t("core:last_reminded", {
                      time: renderTimeAgo(item.datetime_last_reminder),
                    })
                  : ""
              }
            />
          )}
        </DropdownMenu>
      );
    },
    [
      review.id,
      review.status,
      handleOpenParticipantShareSetting,
      handleOpenEditReviewersModal,
      openModal,
      renderTimeAgo,
      t,
    ]
  );

  return (
    <Box>
      <Stack direction={"horizontal"}>
        <Search
          value={searchTerm}
          onInput={(e) => {
            setSearchTerm(e.currentTarget.value);
          }}
          type={"text"}
          placeholder={t("common:search_by_name")}
        />
      </Stack>
      <Table
        sorting={sorting}
        onSortUpdated={onApplySorting}
        data={participantData?.result.items || []}
        columns={columns}
        renderRowAction={renderRowAction}
        showSkeleton={isLoading}
        showNotFoundError={filterCount > 0 && participantData?.result.metadata.total_items === 0}
        notFoundComponent={<EmptyStateNothingFound />}
        layout={"auto"}
      />
      <PaginationBlock
        metadata={participantData?.result.metadata}
        showOnSinglePage={true}
        onPageChange={(page) => {
          setSearchParams((searchParams) => {
            searchParams.set("page", page.toString());
            return searchParams;
          });
        }}
      />
    </Box>
  );
};
